import { SKY_WORLD, SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import {
  skyFlightFrameFromTangent,
  skyFlightPointToWorld,
  skyFlightPointToLocal,
} from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import { skyAuthoredPoseContact } from '../../../packages/domain/src/115-sky-authored-pose.mjs';

/** Original carrier bounds and the real orbital pilot share a close shoulder.
 * No independent orbit, camera-driven actor scale or remote cursor zoom. A
 * distant off-axis lamp is reached by actually orbiting toward it; the lens
 * includes visible objectives while preserving a readable nearby pilot. */
export function skyBrideCameraFrame(state, aspect, pointFov, aim) {
  const boss = state.boss,
    bounds = boss?.authoredPose?.bounds;
  if (!boss?.mourning || !bounds) return null;
  const basis =
    state.flightFrame ??
    skyFlightFrameFromTangent(
      { x: state.x, y: state.y, z: state.pilotZ ?? SKY_WORLD.pilotZ },
      { x: 0, y: 0, z: -1 },
    );
  const at = (x, y, z) => skyFlightPointToWorld(basis, { x, y, z });
  const eye = at(0, 0.7, 0);
  let position = at(aspect < 1 ? 0.45 : 0.9, 1.15, 5.85);
  const lamp = boss.authoredProps.find((p) => p.mourningLamp);
  const obstruction = lamp && skyAuthoredPoseContact(lamp.authoredPose, eye, position, null, 0.18);
  if (obstruction) {
    const fraction = Math.max(0, obstruction.fraction - 0.04);
    position = Object.fromEntries(
      ['x', 'y', 'z'].map((a) => [a, eye[a] + (position[a] - eye[a]) * fraction]),
    );
  }
  const centre = {
    x: (bounds.min[0] + bounds.max[0]) / 2,
    y: (bounds.min[1] + bounds.max[1]) / 2,
    z: (bounds.min[2] + bounds.max[2]) / 2,
  };
  const sole = state.y - SKY_WORLD.pilotAimHeight;
  const pilot = {
    x: state.x,
    y: sole + SKY_WORLD_ASSETS.pilot.height / 2,
    z: state.pilotZ ?? SKY_WORLD.pilotZ,
  };
  const objective = boss.mourning.anchors.some((anchor) => anchor.hp > 0 && anchor.lit)
    ? boss.weakpoint
    : lamp?.weakpoint;
  const pilotPoints = [
    { ...pilot, y: sole },
    { ...pilot, y: sole + SKY_WORLD_ASSETS.pilot.height },
  ];
  const hullPoints = [];
  for (const x of [bounds.min[0], bounds.max[0]])
    for (const y of [bounds.min[1], bounds.max[1]])
      for (const z of [bounds.min[2], bounds.max[2]]) hullPoints.push({ x, y, z });
  const lensBasis = { ...basis, origin: position };
  const selected =
    state.aimPoint && aim && skyFlightPointToLocal(lensBasis, aim)?.z < -0.1 ? aim : null;
  const points = selected ? [...pilotPoints, selected] : [...pilotPoints, centre, ...hullPoints];
  // Centre the angular envelope, not the enormous world's linear AABB. This
  // prioritizes the selected source target during aim and the full arrival
  // silhouette otherwise, with the nearby pilot included in both.
  const local = points.map((p) => skyFlightPointToLocal(lensBasis, p));
  const yawAngles = local.map((p) => Math.atan2(p.x, -p.z));
  const yaw = (Math.min(...yawAngles) + Math.max(...yawAngles)) / 2;
  const pitchAngles = local.map((p) => Math.atan2(p.y, Math.sin(yaw) * p.x - Math.cos(yaw) * p.z));
  const pitch = (Math.min(...pitchAngles) + Math.max(...pitchAngles)) / 2;
  const look = skyFlightPointToWorld(lensBasis, {
    x: Math.sin(yaw) * Math.cos(pitch),
    y: Math.sin(pitch),
    z: -Math.cos(yaw) * Math.cos(pitch),
  });
  const frame = {
    ...position,
    lookX: look.x,
    lookY: look.y,
    lookZ: look.z,
    up: { ...basis.up },
    fov: aspect < 1 ? 64 : 58,
    near: 0.1,
    firstPerson: false,
    maskWeight: 0,
  };
  for (const point of points)
    frame.fov = Math.max(
      frame.fov,
      Math.min(aspect < 1 ? 110 : 100, pointFov(frame, point, aspect, 0.9)),
    );
  // During deliberate aiming, the real target and pilot take priority over
  // forcing the complete ship into view. Add source context only when it fits
  // the same bounded lens; never retreat until the rider becomes a speck.
  for (const point of [...hullPoints, objective].filter(Boolean)) {
    const required = pointFov(frame, point, aspect, 0.9);
    if (required <= (aspect < 1 ? 110 : 100)) frame.fov = Math.max(frame.fov, required);
  }
  return frame;
}
